import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  appointmentUrl,
  locationNavLinks,
  logoImage,
  patientPortalUrl,
  resourceNavLinks,
} from "@/mocks/ppcare";

const mainLinks = [
  { label: "Home", href: "/", isRoute: true },
  { label: "About", href: "#about", isRoute: false },
  { label: "Services", href: "#services", isRoute: false },
  { label: "Providers", href: "#providers", isRoute: false },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const closeAll = () => {
    setMobileOpen(false);
    setOpenMenu(null);
  };

  return (
    <header ref={headerRef} className="contents">
      {/* Utility bar */}
      <div className="w-full bg-primary-950 text-background-50">
        <div className="container flex items-center justify-between py-2">
          <div className="flex items-center gap-5 md:gap-7">
            <a
              href="#contact"
              className="flex items-center gap-2 text-xs md:text-sm transition-opacity hover:opacity-80"
            >
              <i className="ri-phone-line text-base md:text-lg" />
              <span className="font-medium tracking-wide">Contact Us</span>
            </a>
            <a
              href={patientPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs md:text-sm transition-opacity hover:opacity-80"
            >
              <i className="ri-user-3-line text-base md:text-lg" />
              <span className="font-medium tracking-wide">Patient Portal</span>
            </a>
          </div>
          <div className="hidden items-center gap-2 text-xs text-background-300 sm:flex md:text-sm">
            <i className="ri-map-pin-2-line" />
            <span>Boca Raton · Delray Beach · Hallandale</span>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div
        className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
          scrolled
            ? "border-background-200/70 bg-background-50/95 backdrop-blur-md"
            : "border-transparent bg-background-50"
        }`}
      >
        <nav className="container flex h-[70px] items-center justify-between gap-4 md:h-[86px]">
          <Link to="/" className="flex shrink-0 items-center" onClick={closeAll}>
            <img
              src={logoImage}
              alt="Personal Physician Care"
              className="h-12 w-auto object-contain md:h-16"
            />
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            {mainLinks.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="rounded-md px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-600"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="rounded-md px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-600"
                >
                  {link.label}
                </a>
              )
            )}

            {/* Resources dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenMenu(openMenu === "resources" ? null : "resources")}
                className="flex cursor-pointer items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-600"
              >
                Resources
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    openMenu === "resources" ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openMenu === "resources" && (
                <div className="absolute left-0 top-full mt-2 w-52 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
                  {resourceNavLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={closeAll}
                      className="block px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Locations dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenMenu(openMenu === "locations" ? null : "locations")}
                className="flex cursor-pointer items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-600"
              >
                Locations
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    openMenu === "locations" ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openMenu === "locations" && (
                <div className="absolute left-0 top-full mt-2 w-52 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
                  {locationNavLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={closeAll}
                      className="block px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#contact"
              className="rounded-md px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-600"
            >
              Contact
            </a>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={appointmentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 md:inline-flex"
            >
              Appointment
            </a>
            <button
              type="button"
              aria-label="Open cart"
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-shopping-bag-3-line text-lg" />
            </button>
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMobileOpen((value) => !value)}
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            >
              <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
            </button>
          </div>
        </nav>

        {/* Mobile panel */}
        {mobileOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <div className="container flex flex-col py-3">
              {mainLinks.map((link) =>
                link.isRoute ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={closeAll}
                    className="border-b border-background-100 py-3 text-sm font-medium text-foreground-800"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={closeAll}
                    className="border-b border-background-100 py-3 text-sm font-medium text-foreground-800"
                  >
                    {link.label}
                  </a>
                )
              )}
              <p className="pt-4 text-xs font-semibold uppercase tracking-widest text-accent-600">
                Resources
              </p>
              {resourceNavLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={closeAll}
                  className="py-2 text-sm text-foreground-700"
                >
                  {link.label}
                </a>
              ))}
              <p className="pt-4 text-xs font-semibold uppercase tracking-widest text-accent-600">
                Locations
              </p>
              {locationNavLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={closeAll}
                  className="py-2 text-sm text-foreground-700"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={closeAll}
                className="py-2 text-sm text-foreground-700"
              >
                Contact
              </a>
              <a
                href={appointmentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 mb-2 inline-flex justify-center whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50"
              >
                Appointment
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}